import { useEffect, useState } from "react";
import { Menu, X, Ship } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBooking } from "@/context/BookingContext";

const NAV_LINKS = [
  { label: "FreightIQ™", href: "#freightiq" },
  { label: "Workspace", href: "#workspace" },
  { label: "Tracking", href: "#tracking" },
  { label: "Inspection", href: "#inspection" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { open, openWith } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
        >
          <span className="flex items-center justify-center h-8 w-8 rounded-lg bg-[#0B1F3A] text-white">
            <Ship className="h-4 w-4" strokeWidth={2} />
          </span>
          <span className="font-display font-extrabold text-lg tracking-tight text-[#0B1F3A]">
            DeMaxtore
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className="text-sm font-medium text-slate-600 hover:text-[#0B1F3A] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Button
            data-testid="nav-cta-workspace"
            variant="ghost"
            onClick={() => openWith("workspace")}
            className="text-[#0B1F3A] hover:bg-slate-100 hover:text-[#0B1F3A] font-semibold"
          >
            Create Workspace
          </Button>
          <Button
            data-testid="nav-cta-book"
            onClick={open}
            className="bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white font-semibold rounded-full px-5 shadow-sm"
          >
            Book Your Shipment
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 bg-white text-[#0B1F3A]"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-200">
          <div className="px-4 py-4 space-y-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {l.label}
              </a>
            ))}
            <div className="pt-3 grid grid-cols-1 gap-2">
              <Button
                data-testid="nav-mobile-cta-workspace"
                variant="outline"
                onClick={() => {
                  setMobileOpen(false);
                  openWith("workspace");
                }}
                className="w-full border-slate-200 text-[#0B1F3A]"
              >
                Create Workspace
              </Button>
              <Button
                data-testid="nav-mobile-cta-book"
                onClick={() => {
                  setMobileOpen(false);
                  open();
                }}
                className="w-full bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white"
              >
                Book Your Shipment
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
